/**
 * ReliquaryScene — dedicated Phaser scene for the server-wide Reliquary.
 *
 * Per `the-one-and-the-approach.md` §4.3, the Reliquary is the "only reward"
 * of the long-game. Every player sees the same Reliquary, populated by all
 * Relics ever granted on this server. Round 20 Phase F replaces the journal
 * Relics tab with this full scene.
 *
 * Opens with R-key from the Overworld; Esc or R closes it. The scene
 * requests a server-wide snapshot on open and renders each Relic as a named
 * slot with authored prose + atmospheric-effect-key description. Effect
 * rendering is deferred (atmospheric-effect-keys stay descriptive text).
 *
 * Pattern: paused-parent overlay (same as SleepOverlayScene / JournalScene).
 */

import * as Phaser from 'phaser';
import { PAINTED_WORLD_COORDINATOR_SCENE_KEY, RELIQUARY_SCENE_KEY } from './sceneKeys';
import {
  MSG_RELIQUARY_REQUEST,
  MSG_RELIQUARY_SNAPSHOT,
  type ReliquaryEntry,
  type ReliquarySnapshot,
} from '@game/shared';

const TITLE_FONT = "'Almendra', serif";
const COLOR_INK = '#eae0c8';
const COLOR_INK_MUTED = '#a89b80';
const COLOR_INK_HOVER = '#ffffff';
const COLOR_BG = 0x1a1a2e;
const CONTENT_WIDTH = 640;
const CONTENT_PAD = 32;
const CONTENT_TOP = 100;
const CONTENT_BOTTOM_MARGIN = 40;

/** Tier display labels — quiet, descriptive. */
function tierLabel(tier: ReliquaryEntry['tier']): string {
  switch (tier) {
    case 'guardian':
      return 'Guardian Relic';
    case 'deep':
      return 'Deep Expedition';
    case 'medium':
      return 'Expedition';
    case 'shallow':
      return 'Found along the way';
    case 'rumored':
      return 'Rumored';
  }
}

export class ReliquaryScene extends Phaser.Scene {
  public static readonly KEY = RELIQUARY_SCENE_KEY;

  private contentContainer: Phaser.GameObjects.Container | null = null;
  private contentMask: Phaser.Display.Masks.GeometryMask | null = null;
  private scrollOffset = 0;
  private contentHeight = 0;
  private entries: ReliquaryEntry[] = [];
  private snapshotListener: ((payload: unknown) => void) | null = null;

  constructor() {
    super({ key: RELIQUARY_SCENE_KEY });
  }

  create(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    // Semi-transparent backdrop — blocks input to the paused world scene.
    const backdrop = this.add
      .rectangle(cx, cy, this.scale.width, this.scale.height, 0x000000, 0.78)
      .setInteractive();
    backdrop.setScrollFactor(0);

    // Title.
    this.add
      .text(cx, 40, 'The Reliquary', {
        fontFamily: TITLE_FONT,
        fontSize: '36px',
        color: COLOR_INK,
      })
      .setOrigin(0.5, 0.5);

    // Subtitle — server-wide framing.
    this.add
      .text(cx, 68, 'Everything kept safe by everyone who travelled here.', {
        fontFamily: TITLE_FONT,
        fontSize: '15px',
        color: COLOR_INK_MUTED,
        fontStyle: 'italic',
      })
      .setOrigin(0.5, 0.5);

    // Close button.
    this.makeButton(
      this.scale.width / 2 + CONTENT_WIDTH / 2,
      40,
      'close',
      () => this.dismiss(),
    );

    // Content scroll area.
    this.setupContentArea();

    // Esc / R close.
    const esc = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    esc?.on('down', () => this.dismiss());
    const rKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.R);
    rKey?.on('down', () => this.dismiss());

    // Mouse-wheel scroll.
    this.input.on('wheel', (_p: unknown, _gx: unknown, _gy: unknown, _gz: unknown, deltaY: number) => {
      this.applyScroll(deltaY * 0.5);
    });

    // Listen for the reliquary snapshot on the game-event bus.
    this.snapshotListener = (payload: unknown) => {
      if (!payload || typeof payload !== 'object') return;
      const snap = payload as ReliquarySnapshot;
      if (!Array.isArray(snap.entries)) return;
      this.entries = snap.entries;
      this.renderEntries();
    };
    this.game.events.on('reliquary:snapshot', this.snapshotListener);

    // Request the snapshot.
    const overworld = this.scene.get(PAINTED_WORLD_COORDINATOR_SCENE_KEY) as
      | (Phaser.Scene & { sendRoomMessage?: (type: string, payload: Record<string, unknown>) => void })
      | null;
    overworld?.sendRoomMessage?.(MSG_RELIQUARY_REQUEST, {});

    // Render empty state while waiting.
    this.renderEntries();

    // Clean up on shutdown.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (this.snapshotListener) {
        this.game.events.off('reliquary:snapshot', this.snapshotListener);
        this.snapshotListener = null;
      }
      if (this.contentMask) {
        this.contentMask.destroy();
        this.contentMask = null;
      }
      this.contentContainer = null;
    });
  }

  private setupContentArea(): void {
    const left = this.scale.width / 2 - CONTENT_WIDTH / 2 + CONTENT_PAD;
    const container = this.add.container(left, CONTENT_TOP);
    container.setScrollFactor(0);
    this.contentContainer = container;

    const maskW = CONTENT_WIDTH - CONTENT_PAD * 2;
    const maskH = this.scale.height - CONTENT_TOP - CONTENT_BOTTOM_MARGIN;
    const maskShape = this.make.graphics({}, false);
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(left, CONTENT_TOP, maskW, maskH);
    const mask = maskShape.createGeometryMask();
    container.setMask(mask);
    this.contentMask = mask;
  }

  private renderEntries(): void {
    if (!this.contentContainer) return;
    this.contentContainer.removeAll(true);
    this.scrollOffset = 0;
    this.contentContainer.setY(CONTENT_TOP);

    if (this.entries.length === 0) {
      const empty = this.add.text(
        0,
        24,
        'The Reliquary is empty. No relics have been found yet.\nKeep going. Things kept safe travel far.',
        {
          fontFamily: TITLE_FONT,
          fontSize: '17px',
          color: COLOR_INK_MUTED,
          fontStyle: 'italic',
          wordWrap: { width: CONTENT_WIDTH - CONTENT_PAD * 2 },
        },
      );
      this.contentContainer.add(empty);
      this.contentHeight = 0;
      return;
    }

    const wrapWidth = CONTENT_WIDTH - CONTENT_PAD * 2;
    let y = 0;

    for (const entry of this.entries) {
      // Relic name heading.
      const heading = this.add.text(0, y, entry.name, {
        fontFamily: TITLE_FONT,
        fontSize: '18px',
        color: COLOR_INK,
        fontStyle: 'bold',
      });
      this.contentContainer.add(heading);
      y += 24;

      // Source provenance.
      const source = this.add.text(0, y, entry.source, {
        fontFamily: TITLE_FONT,
        fontSize: '13px',
        color: COLOR_INK_MUTED,
      });
      this.contentContainer.add(source);
      y += 18;

      // Authored flavor prose.
      const flavor = this.add.text(0, y, entry.flavor, {
        fontFamily: TITLE_FONT,
        fontSize: '15px',
        color: COLOR_INK,
        fontStyle: 'italic',
        wordWrap: { width: wrapWidth },
      });
      this.contentContainer.add(flavor);
      y += flavor.height + 8;

      // Atmospheric-effect-key description (descriptive text, not rendered).
      const effect = this.add.text(0, y, entry.effect, {
        fontFamily: TITLE_FONT,
        fontSize: '13px',
        color: COLOR_INK_MUTED,
        wordWrap: { width: wrapWidth },
      });
      this.contentContainer.add(effect);
      y += effect.height + 6;

      // Tier footnote.
      const tier = this.add.text(0, y, tierLabel(entry.tier), {
        fontFamily: TITLE_FONT,
        fontSize: '12px',
        color: COLOR_INK_MUTED,
      });
      this.contentContainer.add(tier);
      y += 20;

      // Separator space.
      y += 16;
    }

    this.contentHeight = Math.max(0, y - (this.scale.height - CONTENT_TOP - CONTENT_BOTTOM_MARGIN));
  }

  private applyScroll(deltaY: number): void {
    if (!this.contentContainer) return;
    this.scrollOffset = Math.max(0, Math.min(this.contentHeight, this.scrollOffset + deltaY));
    this.contentContainer.setY(CONTENT_TOP - this.scrollOffset);
  }

  private dismiss(): void {
    this.scene.resume(PAINTED_WORLD_COORDINATOR_SCENE_KEY);
    this.scene.stop(RELIQUARY_SCENE_KEY);
  }

  private makeButton(x: number, y: number, text: string, onClick: () => void): void {
    const hit = this.add
      .rectangle(x, y, 80, 28, 0x000000, 0)
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    const label = this.add.text(x, y, text, {
      fontFamily: TITLE_FONT,
      fontSize: '18px',
      color: COLOR_INK_MUTED,
      fontStyle: 'italic',
    });
    label.setOrigin(1, 0.5);

    hit.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
    hit.on('pointerout', () => label.setColor(COLOR_INK_MUTED));
    hit.on('pointerdown', onClick);
  }
}
